import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatBrDateTime } from "@/utils/br-datetime";
import { getCurrentPosition, geolocationErrorMessage } from "@/utils/geo.utils";
import { Icon } from "@/components/ui/icon"

type CheckInResult = {
  checkedInAt: string;
  checkInPoints?: number;
  earlyBonus?: boolean;
};

type UseEventSelfCheckInFlowOptions = {
  checkedInAt?: string | null;
  onCheckIn: (latitude: number, longitude: number) => Promise<CheckInResult>;
  onSuccess?: (checkedInAt: string) => void;
  /** Busca horário quando a API informa check-in duplicado sem dado local. */
  onResolveAlreadyCheckedIn?: () => Promise<string | null | undefined>;
  countsForRanking?: boolean;
};

function formatCheckInPoints(value: number) {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatCheckedInAtLabel(checkedInAt: string) {
  return formatBrDateTime(checkedInAt, {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function useEventSelfCheckInFlow({
  checkedInAt,
  onCheckIn,
  onSuccess,
  onResolveAlreadyCheckedIn,
  countsForRanking = false,
}: UseEventSelfCheckInFlowOptions) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMode, setSuccessMode] = useState<"new" | "already">("new");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<CheckInResult | null>(null);
  const [knownCheckedInAt, setKnownCheckedInAt] = useState<string | null>(
    checkedInAt ?? null,
  );

  useEffect(() => {
    if (checkedInAt) {
      setKnownCheckedInAt(checkedInAt);
    }
  }, [checkedInAt]);

  const showAlreadyDone = (at: string, checkInPoints?: number) => {
    setLastResult({ checkedInAt: at, checkInPoints });
    setSuccessMode("already");
    setSuccessOpen(true);
  };

  const openConfirm = () => {
    if (knownCheckedInAt) {
      showAlreadyDone(knownCheckedInAt);
      return;
    }
    setConfirmOpen(true);
  };

  const openAlreadyDone = () => {
    if (!knownCheckedInAt) return;
    showAlreadyDone(knownCheckedInAt);
  };

  const runCheckIn = async () => {
    try {
      setLoading(true);
      const position = await getCurrentPosition();
      const result = await onCheckIn(
        position.coords.latitude,
        position.coords.longitude,
      );
      setConfirmOpen(false);
      setKnownCheckedInAt(result.checkedInAt);
      setLastResult(result);
      setSuccessMode("new");
      setSuccessOpen(true);
      onSuccess?.(result.checkedInAt);
    } catch (error: unknown) {
      const geoError = error as GeolocationPositionError;
      if (typeof geoError?.code === "number") {
        toast.error(geolocationErrorMessage(geoError));
        return;
      }
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        (error as Error)?.message ||
        "Erro ao realizar check-in. Tente novamente.";

      if (apiMessage.toLowerCase().includes("já fez check-in")) {
        setConfirmOpen(false);
        let at = knownCheckedInAt;
        if (!at && onResolveAlreadyCheckedIn) {
          at = (await onResolveAlreadyCheckedIn()) ?? null;
          if (at) setKnownCheckedInAt(at);
        }
        if (at) {
          showAlreadyDone(at);
          onSuccess?.(at);
        } else {
          toast.error("Você já fez check-in neste evento.");
        }
        return;
      }

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmDescription = countsForRanking
    ? "Sua localização será usada para validar a presença no jogo. Check-ins válidos geram pontos no ranking do time."
    : "Sua localização será usada para validar a presença no local do evento.";

  const checkedInAtLabel =
    lastResult?.checkedInAt && lastResult.checkedInAt.trim()
      ? formatCheckedInAtLabel(lastResult.checkedInAt)
      : null;

  const dialogs = (
    <>
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => !loading && setConfirmOpen(false)}
        onConfirm={runCheckIn}
        title="Confirmar check-in"
        description={confirmDescription}
        confirmText="Confirmar check-in"
        isLoading={loading}
      />

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent size="sm" className="gap-0 p-0">
          <DialogHeader className="border-b border-border px-6 py-5 text-left">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <Icon name="check_circle" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-lg">
                  {successMode === "already"
                    ? "Check-in já realizado"
                    : "Check-in confirmado"}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm leading-relaxed">
                  {successMode === "already"
                    ? "Sua presença neste evento já havia sido registrada."
                    : "Sua presença foi registrada com sucesso."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-4 px-6 py-5">
            {checkedInAtLabel ? (
              <div className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Horário do check-in
                </p>
                <p className="mt-1 text-base font-bold text-foreground">{checkedInAtLabel}</p>
              </div>
            ) : null}
            {successMode === "new" &&
            countsForRanking &&
            lastResult?.checkInPoints != null &&
            lastResult.checkInPoints > 0 ? (
              <p className="text-sm text-muted-foreground">
                +{formatCheckInPoints(lastResult.checkInPoints)} pts no ranking deste jogo
                {lastResult.earlyBonus ? " (inclui bônus por chegar cedo!)" : ""}.
              </p>
            ) : null}
            <Button
              type="button"
              className="h-12 w-full rounded-xl text-base font-bold"
              onClick={() => setSuccessOpen(false)}
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    openConfirm,
    openAlreadyDone,
    loading,
    dialogs,
    lastResult,
    successOpen,
    knownCheckedInAt,
  };
}
